<template>
  <div class="marketplace-container">
    <div class="nav-row-desktop">
      <h1 class="page-header text-center">brands marketplace</h1>
      <div class="links-wrapper add-spacing">
        <a target="_blank" :href="demoLink">need more info?</a>
        <a class="refer-brand" @click="showSimpleModal = true">refer a brand</a>
      </div>
    </div>

    <simple-modal
      v-if="showSimpleModal"
      class="refer-brand-modal"
      title="refer a brand"
      @rejected="rejectedModal"
      @resolved="resolvedModal"
    >
      <p class="text-center">enter brand information in the fields below</p>
      <section class="refer-brand-wrapper">
        <label>brand name</label>
        <input
          name="brand_name"
          @blur="requiredField('brand_name', brandName)"
          type="text"
          :class="{ 'refer-brand-error' : emptyBrandName }"
          maxlength="100"
          v-model.trim="brandName">
        <span class="danger" v-if="emptyBrandName">brand name required</span>
        <label>contact first name</label>
        <input
          @blur="requiredField('contact_first_name', brandContactFirstName)"
          name="contact_first_name"
          type="text"
          :class="{ 'refer-brand-error' : emptyBrandContactFirstName }"
          maxlength="100"
          v-model.trim="brandContactFirstName">
        <span class="danger" v-if="emptyBrandContactFirstName">contact first name required</span>
        <label>contact last name</label>
        <input
          @blur="requiredField('contact_last_name', brandContactLastName)"
          name="contact_last_name"
          type="text"
          :class="{ 'refer-brand-error' : emptyBrandContactLastName }"
          maxlength="100"
          v-model.trim="brandContactLastName">
        <span class="danger" v-if="emptyBrandContactLastName">contact last name required</span>
        <label>phone number</label>
        <input
          :class="{ 'refer-brand-error' : !validPhoneNumber && brandPhoneNumber.length}"
          name="phone_number"
          type="tel"
          maxlength="10"
          @blur="validatePhoneNumber()"
          v-model.trim="brandPhoneNumber">
        <label>email address</label>
        <input
          :class="{ 'refer-brand-error' : !validEmail && brandEmail.length }"
          name="email"
          type="email"
          @blur="validateEmail()"
          v-model.trim="brandEmail">
        <p v-if="!isContactNamesValid" class="error-message">first and last names required</p>
        <p v-if="!isContactInfoValid" class="error-message">phone number or email address required</p>
        <p v-if="!validEmail || !validPhoneNumber" class="error-message">{{errorMessageModal}}</p>
      </section>
    </simple-modal>

    <section class="nav-display-container">
      <p v-if="errorMessage">{{errorMessage}}</p>
      <brands-marketplace-display
        class="add-spacing"
        v-if="selected"
        @closeModal="showModal = false"
        :showModal.sync="showModal"
        :isMobile.sync="isMobile"
        :marketplaceDisplay="marketplaceDisplay"
      />

      <div class="nav-links" :class="{ 'add-width': !selected, 'w-full': selected }">
        <brands-marketplace-navigation
          v-if="marketplaceDataLoaded"
          :showStateNav="showStateNav"
          :showSponsoredNav="showSponsoredNav"
          :showStoreNav="showStoreNav"
          @scrollMeTo="scrollMeToClick"
        />
      </div>
    </section>

    <div class="nav-row-mobile">
      <h3>brands marketplace</h3>
      <p><a target="_blank" :href="demoLink">need more info?</a></p>
      <p class="refer-brand" @click="showSimpleModal = true">refer a brand</p>
    </div>

    <div class="brand-carousels" :class="{ 'add-padding': selected }" :style="{ minHeight: carouselsHeight + 'px' }">
      <div v-if="stateSlides && marketplaceDataLoaded">
        <span ref="top"></span>
        <section class="states-list" :class="{ 'defaultState': index === 0, 'states': !isActive, 'stateSpacing': selected }" v-for="(state, index) in stateSlides" :key="index">
          <p v-if="state.slides && marketplaceDataLoaded" class="carousel-title">{{state.name}} cannabis brands</p>
          <carousel :slides="state.slides"
                :swiperOptions="swiperOptions"
                class="pt-3">
            <template #swiper-slide-content="{ slide }">
              <div class="img-container inline-block relative" @click="syncSlide(slide, state)">
                <div v-if="slide.isNew" class="is-new absolute z-1">
                  <span class="new-circle">new</span>
                </div>
                <img v-if="slide.imgSrc"
                    class="slide-image"
                    :class="{ 'shadow-border' : selected === slide }"
                    :src="slide.imgSrc"
                    :alt="slide.imgSrc">
                <p v-if="slide.label" class="slide-label text-blue text-center">{{ slide.label }}</p>
              </div>
            </template>
          </carousel>
          <p v-if="index === 0" @click="isActive = !isActive" class="initial" :class="{ 'active': isActive }">
            <span v-if="isActive">-</span>
            <span v-else>+</span>
            see all states
          </p>
        </section>
      </div>

      <div v-if="sponsoredSlides && marketplaceDataLoaded">
        <section class="previously-sponsored" ref="sponsored">
          <p v-if="sponsoredSlides.slides && marketplaceDataLoaded" class="carousel-title">{{sponsoredSlides.name}}</p>
          <carousel :slides="sponsoredSlides.slides"
                :swiperOptions="swiperOptions"
                class="pt-3">
            <template #swiper-slide-content="{ slide }">
              <div class="img-container inline-block relative" @click="syncSlide(slide, sponsoredSlides)">
                <div v-if="slide.isNew" class="is-new absolute z-1">
                  <span class="new-circle">new</span>
                </div>
                <img v-if="slide.imgSrc"
                    class="slide-image"
                    :class="{ 'shadow-border' : selected === slide }"
                    :src="slide.imgSrc"
                    :alt="slide.imgSrc">
                <p v-if="slide.label" class="slide-label text-blue text-center">{{ slide.label }}</p>
              </div>
            </template>
          </carousel>
        </section>
      </div>

      <div v-if="storeSlides && marketplaceDataLoaded">
        <section class="in-store" ref="inStore">
            <p v-if="storeSlides.slides && marketplaceDataLoaded" class="carousel-title">{{storeSlides.name}}</p>
            <carousel :slides="storeSlides.slides"
                :swiperOptions="swiperOptions"
                class="pt-3">
            <template #swiper-slide-content="{ slide }">
              <div class="img-container inline-block relative" @click="syncSlide(slide, storeSlides)">
                <div v-if="slide.isNew" class="is-new absolute z-1">
                  <span class="new-circle">new</span>
                </div>
                <img v-if="slide.imgSrc"
                    class="slide-image"
                    :class="{ 'shadow-border' : selected === slide }"
                    :src="slide.imgSrc"
                    :alt="slide.imgSrc">
                <p v-if="slide.label" class="slide-label text-blue text-center">{{ slide.label }}</p>
              </div>
            </template>
          </carousel>
          </section>
      </div>
    </div>
  </div>
</template>

<script>
  import { brandsMarketplaceAPI } from "../../services/api/brandsMarketplace"
  import BrandsMarketplaceNavigation from '../../components/BrandsMarketplace/BrandsMarketplaceNavigation'
  import BrandsMarketplaceDisplay from '../../components/BrandsMarketplace/BrandsMarketplaceDisplay'
  import { Carousel, SimpleModal, UnexpectedError, ErrorBanner } from '@springbig/sb-components'
  import smoothscroll from 'smoothscroll-polyfill';
  import { isWithinInterval, subMonths, startOfDay } from 'date-fns'
  import EmailValidator from 'email-validator'

  export default {
    name: 'BrandsMarketplace',

    components: {
      BrandsMarketplaceNavigation,
      BrandsMarketplaceDisplay,
      Carousel,
      SimpleModal,
      ErrorBanner
    },

    data() {
      return {
        brandContactFirstName: '',
        brandContactLastName: '',
        brandEmail: '',
        brandName: '',
        brandPhoneNumber: '',
        isBrandsLoading: null,
        carouselsHeight: null,
        isContactInfoValid: true,
        isContactNamesValid: true,
        demoLink: 'http://calendly.com/mkilgo',
        emptyBrandContactFirstName: null,
        emptyBrandContactLastName: null,
        emptyBrandName: null,
        errorMessage: '',
        isActive: false,
        isMobile: false,
        marketplaceDataLoaded: false,
        marketplaceDisplay: null,
        model: 0,
        previousRow: null,
        selected: null,
        showModal: false,
        showSimpleModal: false,
        showSponsoredNav: false,
        showStateNav: false,
        showStoreNav: false,
        sponsoredSlides: {},
        stateSlides: [],
        storeSlides: {},
        swiperOptions: {
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
          },
          grabCursor: true,
          breakpoints: {
            1650: {
              slidesPerView: 5,
              slidesPerGroup: 5,
              spaceBetween: 10
            },
            1540: {
                slidesPerView: 5,
                spaceBetween: 10,
                slidesPerGroup: 5
            },
            1224: {
                slidesPerView: 5,
                spaceBetween: 10,
                slidesPerGroup: 5
            },
            768: {
                slidesPerView: 3,
                spaceBetween: 10,
                slidesPerGroup: 3
            },
            640: {
                slidesPerView: 2,
                spaceBetween: 10,
                slidesPerGroup: 2
            },
            320: {
                slidesPerView: 1,
                spaceBetween: 10,
                slidesPerGroup: 1
            }
          },
        },
        validEmail: null,
        validPhoneNumber: null
      }
    },

    created() {
      this.getMarketplaceDisplay()
      smoothscroll.polyfill()

      // Use window listener since height is still incorrect in mounted and this.$nextTick
      window.addEventListener('load', this.calculateCarouselsHeight)
    },

    computed: {
      errorMessageModal: function() {
        if (!this.validEmail && !this.validPhoneNumber && this.brandPhoneNumber.length && this.brandEmail.length) {
          return `invalid phone number and email address`
        } else if (!this.validPhoneNumber && this.brandPhoneNumber.length) {
          return `invalid phone number`
        } else if (!this.validEmail && this.brandEmail.length) {
          return `invalid email address`
        }
      }
    },

    beforeDestroy () {
      if (typeof window === 'undefined') return
      window.removeEventListener('resize', this.onResize, { passive: true })
      window.removeEventListener('load', this.calculateCarouselsHeight)
    },

    mounted () {
      this.onResize()
      window.addEventListener('resize', this.onResize, { passive: true })
    },

    methods: {
      calculateCarouselsHeight() {
        const navDivHeight = document.querySelector('.nav-display-container').offsetHeight + document.querySelector('.nav-row-desktop').offsetHeight
        const bodyHeight = document.body.offsetHeight
        const comparedHeight = window.innerHeight > bodyHeight ? window.innerHeight : bodyHeight
        this.carouselsHeight = comparedHeight - navDivHeight
      },

      async getMarketplaceDisplay() {
        this.isBrandsLoading = true
        try {
          const marketplaceDisplay = await brandsMarketplaceAPI.getAllBrandDisplays()
          if (marketplaceDisplay[0].states.length) {
            this.showStateNav = true
            marketplaceDisplay[0].states.forEach((element) => {
              const keyName = Object.keys(element)[0]
              const state = {
                name: keyName,
                slides: []
              }

              for (let index = 0; index < element[keyName].length; index++) {
                const stateObject = element[keyName][index]
                const createdAt = new Date(stateObject.advertiser_created_at)
                const today = new Date()
                const threeMonthsAgo = subMonths(new Date(), 3)
                const monthResult = isWithinInterval(createdAt, {
                  start: threeMonthsAgo,
                  end: today
                })

                const newState = {
                  label: stateObject.display_name,
                  imgSrc: stateObject.image,
                  id: stateObject.id,
                  createdAt: stateObject.advertiser_created_at,
                }
                if (monthResult) newState["isNew"] = true
                state.slides.push(newState)
              }
              this.stateSlides.push(state)
            })
          }
          if (marketplaceDisplay[1].previously_sponsored.length) {
            this.showSponsoredNav = true
            this.createSlidesArray('sponsoredSlides', 'previously sponsored', marketplaceDisplay[1].previously_sponsored)
          }
          if (marketplaceDisplay[2].in_store.length) {
            this.showStoreNav = true
            this.createSlidesArray('storeSlides', 'in-store brands', marketplaceDisplay[2].in_store)
          }
          this.marketplaceDataLoaded = true
        } catch (error) {
            this.errorMessage = error
        }
        this.isBrandsLoading = false
      },

      createSlidesArray(dataProp, name, arr) {
        let state = {
          name: name,
          slides: []
        }

        arr.forEach((element) => {
          state.slides.push({
            label: element.display_name,
            imgSrc: element.image,
            id: element.id,
            createdAt: element.advertiser_created_at
          })
        })

        this[dataProp] = state
      },

      onResize () {
        this.isMobile = window.innerWidth < 900
      },

      async getBrandDisplay(id) {
        this.isBrandsLoading = true
        try {
          const marketplaceBrand = await brandsMarketplaceAPI.getBrandDisplay(id)
          this.marketplaceDisplay = marketplaceBrand
          this.calculateCarouselsHeight()
        } catch (error) {
          this.errorMessage = error
        }
        this.isBrandsLoading = false
      },

      scrollMeToClick(refName) {
        const element = this.$refs[refName]
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start', alignToTop: false })
      },

      rejectedModal() {
        this.clearFields()
        this.showSimpleModal = false
      },

      async resolvedModal() {
        this.requiredField('brand_name', this.brandName)
        this.requiredField('contact_first_name', this.brandContactFirstName)
        this.requiredField('contact_last_name', this.brandContactLastName)

        const contactNamesIsValid = this.brandContactFirstName.length && this.brandContactLastName.length
        const contactInfoIsValid = this.brandPhoneNumber.length || this.brandEmail.length
        this.isContactInfoValid = contactInfoIsValid
        this.isContactNamesValid = contactNamesIsValid

        try {
          this.$store.dispatch('DISMISSED_MODAL');
          this.$store.dispatch('DISMISS_ERRORS', { type: UnexpectedError })
          if (this.brandName.length && this.brandContactFirstName.length && this.brandContactLastName.length && this.isContactInfoValid && (this.validPhoneNumber || this.validEmail)) {
            const response = await brandsMarketplaceAPI.postBrandReferral({
              brand_name: this.brandName,
              contact_name: this.brandContactFirstName,
              contact_last_name: this.brandContactLastName,
              phone_number: this.brandPhoneNumber,
              email: this.brandEmail
            })

            if (response.status === 201) {
              this.showSimpleModal = false
              this.clearFields()
            }
          }
        } catch (error) {
          this.$store.dispatch('ADD_ERROR', new UnexpectedError('500'))
        }
      },

      clearFields() {
        this.brandName = ''
        this.brandContactFirstName = ''
        this.brandContactLastName = ''
        this.brandPhoneNumber = ''
        this.brandEmail = ''
        this.emptyBrandContactFirstName = null
        this.emptyBrandContactLastName = null
        this.emptyBrandName = null
        this.validEmail = null
        this.validPhoneNumber = null
        this.isContactNamesValid = true
        this.isContactInfoValid = true
      },

      requiredField(name, val) {
        if (name === 'contact_first_name')
          this.emptyBrandContactFirstName = !val.length

        if (name === 'contact_last_name')
          this.emptyBrandContactLastName = !val.length

        if (name === 'brand_name')
          this.emptyBrandName = !val.length
      },

      validateEmail() {
        if (this.brandEmail.length) {
          this.validEmail = EmailValidator.validate(this.brandEmail)

          if (this.validEmail) {
            this.isContactInfoValid = true
          } else {
            this.validEmail = false
            this.isContactInfoValid = false
          }
        }
      },

      validatePhoneNumber() {
        if (this.brandPhoneNumber.length) {
          const regex = /^\d{10}$/
          this.validPhoneNumber = regex.test(this.brandPhoneNumber)

          if (this.validPhoneNumber) {
            this.isContactInfoValid = true
          }
        } else {
          this.validPhoneNumber = false
        }
      },

      syncSlide(newSlide, previousSlide) {
        if (this.selected && this.selected.id === newSlide.id && this.previousRow === previousSlide.name) {
          this.selected = null
          this.previousRow = null
          return false
        } else {
          this.selected = newSlide
          this.previousRow = previousSlide.name
        }

        if (this.isMobile) {
          this.showModal = true
        } else {
          this.showModal = false
        }

        this.getBrandDisplay(newSlide.id)
      }
    }
  }
</script>

<style scoped>
  .marketplace-container >>> .display-images-wrapper,
  .nav-display-container .nav-links {
    display: flex;
  }

  .marketplace-container >>> .new-circle {
    background-color: #128ff9;
    color: white;
    border-radius: 50%;
    padding: 8px 4px;
  }

  .marketplace-container >>> .is-new {
    right: -22px;
    top: -4px;
  }

  .nav-row-desktop {
    align-items: center;
    position: fixed;
    padding-top: 2px;
    height: auto;
    z-index: 10;
    background-color: white;
    width: calc(100% - 275px);
    display: flex;
    justify-content: space-between;
    padding: 20px 0 20px 20px;
  }

  .nav-row-desktop p {
    color: #128ff9;
    cursor: pointer;
  }

  .error-banner {
    color: red;
  }

  .refer-brand-error {
    border: 1px solid red;
  }

  .nav-row-mobile {
    display: none;
  }

  .error-message, .danger {
    color: red;
    text-align: center;
  }

  .marketplace-container >>> .refer-brand-modal {
    padding: 0;
    z-index: 99;
  }

  .marketplace-container >>> .refer-brand-modal .sb-button {
    font-family: 'AileronThin';
    color: #FFFFFF;
  }

  .marketplace-container >>> .refer-brand-modal .sb-reject {
    display: none;
  }

  .marketplace-container >>> .refer-brand-modal .text-3xl {
    margin-top: 15px;
    font-weight: 600;
    color: #4A4A4A;
  }

  .marketplace-container .refer-brand-modal h2 {
    font-family: 'AileronThin';
    font-weight: 600;
    color: #4A4A4A;
  }

  .nav-display-container .nav-links {
    justify-content: center;
    border-top: 1px solid #128ff9;
  }

  .marketplace-container >>> .swiper-container,
  .marketplace-container >>> .swiper-wrapper {
    /* override swiper */
    position: unset !important;
  }

  .marketplace-container >>> .swiper-button-next {
    /* override swiper */
    right: 9px;
  }

  .marketplace-container >>> .swiper-button-next:after,
  .marketplace-container >>> .swiper-button-prev:after {
    /* override swiper */
    font-size: 55px !important;
  }

  .marketplace-container >>> .swiper-button-prev {
    /* override swiper */
    left: 19px;
  }

  .marketplace-container >>> .swiper-button-next,
  .marketplace-container >>> .swiper-button-prev {
    /* override swiper */
    top: 117px;
    z-index: 1 !important;
  }

  section.in-store >>> .swiper-button-next,
  section.previously-sponsored >>> .swiper-button-next,
  section.previously-sponsored >>> .swiper-button-prev,
  section.in-store >>> .swiper-button-prev {
    top: 170px;
  }

  .marketplace-container section.defaultState.stateSpacing >>> .swiper-button-next,
  .marketplace-container section.defaultState.stateSpacing >>> .swiper-button-prev {
    top: 623px;
  }

  .marketplace-container section.defaultState >>> .swiper-button-next,
  .marketplace-container section.defaultState >>> .swiper-button-prev {
    top: 318px;
  }

  .marketplace-container section.defaultState >>> .swiper-button-next {
    right: 35px;
  }

  .marketplace-container >>> .swiper-slide img {
    border: 2px solid #128ff9;
    width: 150px;
    border-radius: 20px;
    background-color: white;
    cursor: pointer;
  }

  .marketplace-container >>> .initial {
    color: #128ff9;
    font-size: 18px;
    cursor: pointer;
  }

  .marketplace-container >>> .initial.active {
    color: gray;
  }

  .marketplace-container >>> .swiper-slide img.shadow-border {
    border: 0;
  }

  .refer-brand-modal >>> .bg-white {
    padding: 20px 40px;
    box-shadow: none !important;
  }

  section.previously-sponsored .carousel-title,
  section.in-store .carousel-title {
    top: -10px
  }

  section.previously-sponsored {
    /* scroll-margin-top not supported in safari & scroll-snap-margin-top not working in safari */
      padding-top: 180px;
      margin-top: -180px;
  }

  section.states-list, section.previously-sponsored, section.in-store {
    position: relative;
  }

  section.states {
    display: none;
  }

  section.states.defaultState {
    display: block;
  }

  section.defaultState {
    margin-top: -200px;
    padding-top: 200px;
  }

  section.defaultState.stateSpacing {
    margin-top: -500px;
    padding-top: 500px;
  }

  .nav-display-container {
    background-color: white;
    z-index: 2;
    width: calc(100% - 275px);
    position: fixed;
    top: 0;
    padding-top: 76px;
  }

  .marketplace-container >>> .brand-info {
    flex: 1 0 auto;
  }

  .marketplace-container .page-header, .nav-row-mobile h3 {
    text-align: left;
    font-size: 1.5rem;
    margin-bottom: 0;
  }

  .marketplace-container >>> .brand-info h2 {
    word-break: break-all;
    font-size: 1.5em;
    font-family: 'AileronLight';
  }

  .marketplace-container >>> .brand-info p {
    font-family: 'AileronThin';
    font-size: 20px;
    line-height: 26px;
    word-break: break-word;
  }

  .mobile-header {
    display: none;
  }

  .marketplace-container >>> button.sponsor {
    width: 255px;
    margin-top: 10px;
  }

  .marketplace-container >>> .images-container {
    display: inline-flex;
  }

  .marketplace-container >>> .side-images img {
    width: 55px;
    height: 46.4px;
    border-top-left-radius: 15px;
    border-bottom-left-radius: 15px;
    border: 1px solid #128ff9;
  }

  .carousel-title {
    margin-bottom: 10px;
    font-weight: 300;
    font-family: 'Aileronlight';
  }

  .marketplace-container >>> .swiper-wrapper {
    padding-top: 10px;
  }

  .marketplace-container >>> .slide-label {
    margin: 5px auto;
    word-break: break-word;
    max-width: fit-content;
    font-size: 15px !important;
  }

  .marketplace-container >>> .swiper-button-disabled {
    /* override swiper */
    display: none !important;
  }

  .marketplace-container >>> .new-icon {
    width: 45px;
    background-color: white;
    border-radius: 50%;
    border: 0 !important;
  }

  .marketplace-container >>> .img-container {
    margin-left: 9px;
    margin-right: 9px;
  }

  .marketplace-container {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    height: 100vh;
    padding-right: 0.9375rem;
  }

  .brand-carousels {
    padding: 170px 0 0 20px;
  }

  .brand-carousels.add-padding {
    padding-top: 445px;
  }

  .brands-wrapper {
    padding: 0 50px;
  }

  .add-spacing {
    padding: 0 70px 0 20px;
  }

  .links-wrapper {
    display: flex;
  }

  .links-wrapper a:not(:last-child) {
    margin-right: 50px;
  }

  @media only screen and (max-width: 1400px) {
    .nav-row-desktop {
      max-width: 73%;
    }
  }

  @media only screen and (max-width: 1224px) {
    section.previously-sponsored {
      scroll-margin-top: 220px;
    }

    .nav-display-container {
      margin: 0 0 10px;
    }

    .marketplace-container >>> .brand-info {
      width: 21em;
    }
  }

  @media only screen and (min-width: 1024px) {
    div.brand-carousels {
      overflow-y: auto;
    }

    .marketplace-container >>> .display-images-wrapper {
      padding-bottom: 25px;
    }
  }

  @media only screen and (max-width: 1024px) {
    .marketplace-container {
      padding-left: 0.9375rem;
    }
  }

  @media only screen and (max-width: 1023px) {
    div.brand-carousels {
      margin-top: 100px;
      padding-top: 0;
      height: auto !important;
    }

    section.defaultState {
      margin-top: 0;
      padding-top: 0;
    }

    section.defaultState.stateSpacing {
      margin-top: 0;
      padding-top: 0;
    }

    .marketplace-container section.defaultState >>> .swiper-button-next,
    .marketplace-container section.defaultState.stateSpacing >>> .swiper-button-next,
    .marketplace-container section.defaultState.stateSpacing >>> .swiper-button-prev,
    .marketplace-container section.defaultState >>> .swiper-button-prev {
      top: 165px;
    }

    .brand-carousels.add-padding {
      padding-top: 0;
    }

    .nav-display-container .nav-links {
      border-top: 0;
    }

    .nav-display-container {
      margin: 0;
      padding-top: 0;
    }

    .nav-row-mobile {
      display: block;
      margin-top: 20px;
    }

    .marketplace-container button.sponsor {
      width: 90%;
    }

    .brand-carousels {
      padding-top: 0;
    }

    .marketplace-container >>> .swiper-slide img {
      width: 200px;
      height: 200px;
    }

    .marketplace-container >>> .nav-links,
    .marketplace-container >>> .display-images-wrapper,
    .links-container,
    .marketplace-container >>> .brand-info,
    .marketplace-container >>>  .images-container,
    .nav-row-desktop {
      display: none;
    }
  }

  @media only screen and (max-width: 900px) {
    .carousel-title {
      margin-left: 25px;
      font-size: 22px;
      font-weight: 400;
    }

    div.brand-carousels {
      margin-top: 30px !important;
    }

    .nav-row-mobile p {
      color: #128ff9;
      cursor: pointer;
    }

    .marketplace-container >>> .is-new {
      right: 7px !important;
    }

    .marketplace-container >>> .swiper-slide {
      margin-left: unset;
    }

    .marketplace-container >>> .swiper-button-next {
      /* override swiper */
      right: 9px;
    }

    .marketplace-container >>> .swiper-button-prev {
      /* override swiper */
      left: 9px;
    }

    .marketplace-container >>> .swiper-wrapper {
      padding-top: 20px;
    }

    .marketplace-container >>> .swiper-button-next,
    .marketplace-container >>> .swiper-button-prev {
      /* override swiper */
      top: 54%;
      outline: white !important;
      outline-width: 0 !important;
    }

    section.in-store >>> .swiper-button-next,
    section.previously-sponsored >>> .swiper-button-next,
    section.previously-sponsored >>> .swiper-button-prev,
    section.in-store >>> .swiper-button-prev {
      top: 55%;
    }

    .mobile-header {
      display: inline-block;
      margin-bottom: 70px;
    }

    .mobile-header h1,
    .mobile-header h2 {
      font-family: 'AileronThin';
      display: inline-block;
      color: #128ff9;
    }

    .mobile-header h1 {
      font-size: 2.5em;
      margin-left: 15px;
      margin-bottom: 0;
    }

    .mobile-header h2 {
      font-size: 20px;
      position: relative;
      bottom: 0;
    }

    .marketplace-container >>> .img-container {
      width: 250px;
    }
  }

  @media only screen and (max-width: 568px) {
    .marketplace-container >>> .swiper-slide {
      width: inherit !important;
      margin-left: inherit;
    }

    .refer-brand-modal >>> .bg-white {
      background-color: white;
      margin: 0;
      max-width: none;
      max-height: none;
      height: 100%;
      overflow: auto;
      padding: 20px;
      position: relative;
      border-radius: 0;
      border: 0;
      width: 100%;
    }

    .refer-brand-modal >>> .swiper-button-next {
      /* override swiper */
      right: 25px !important;
    }

    .refer-brand-modal >>> .swiper-button-prev {
      /* override swiper */
      left: 25px !important;
    }
  }

  @media only screen and (min-height: 980px) {
    section.previously-sponsored, section.in-store {
      /* scroll-margin-top not supported in safari & scroll-snap-margin-top not working in safari */
      padding-top: 420px;
      margin-top: -420px;
    }
  }

  @media only screen and (max-width: 900px) {
    section.states:first-child {
      margin-top: 120px;
    }
  }

  @media only screen and (width: 812px) and (height: 375px) and (orientation : landscape) {
    .refer-brand-modal >>> .swiper-button-prev {
      left: -12px!important;
    }

    .marketplace-container >>> .img-container {
      width: 215px;
    }

    .refer-brand-modal >>> .swiper-button-next {
      right: -15px!important;
    }
  }

  @media only screen and (min-width: 568px) and (max-width: 799px) and (orientation : landscape) {
    .refer-brand-modal >>> .swiper-button-prev {
      left: 0 !important;
    }

    .marketplace-container >>> .img-container {
      width: 215px;
    }

    .refer-brand-modal >>> .swiper-button-next {
      right: 0 !important;
    }
  }

  @media only screen and (min-height: 580px) {
    section.previously-sponsored, section.in-store {
      margin-top: -30px;
      padding-top: 30px;
    }
  }
</style>
